import { gql, useQuery } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
} from "@material-ui/core";
import DialogTitle from "components/legacy/Dialog/DialogTitle/DialogTitle";
import { useFlashMessage } from "components/legacy/FlashMessage/FlashMessage";
import Loading from "components/legacy/Loading/Loading";
import LocationForm, {
  EditLocationFormState,
} from "components/legacy/LocationForm/LocationForm";
import {
  ArchiveLocation,
  ArchiveLocationVariables,
  CreateLocation,
  CreateLocationVariables,
  FindLocationToEdit,
  FindLocationToEditVariables,
  UnarchiveLocation,
  UnarchiveLocationVariables,
  UpdateLocation,
  UpdateLocationVariables,
} from "generated/schemaTypes";
import { toNumber } from "lodash";
import { useEffect, useState } from "react";
import useAnalytics from "react-hooks/useAnalytics";
import useMutation from "utils/useMutation";

export const ORGANIZATION_LOCATION_FRAGMENT = gql`
  fragment OrganizationLocation on Location {
    id
    name
    arrivalInstructions
    address
    coordinates
    isArchived
  }
`;

export const FIND_LOCATION = gql`
  query FindLocationToEdit($input: LocationByIdInput!) {
    currentOrganization {
      id
      locationById(input: $input) {
        ...OrganizationLocation
      }
    }
  }
  ${ORGANIZATION_LOCATION_FRAGMENT}
`;

const CREATE_LOCATION = gql`
  mutation CreateLocation($input: CreateLocationMutationInput!) {
    createLocation(input: $input) {
      success
      code
      message
    }
  }
`;

const UPDATE_LOCATION = gql`
  mutation UpdateLocation($input: UpdateLocationMutationInput!) {
    updateLocation(input: $input) {
      success
      code
      message
      location {
        ...OrganizationLocation
      }
    }
  }
  ${ORGANIZATION_LOCATION_FRAGMENT}
`;

const ARCHIVE_LOCATION = gql`
  mutation ArchiveLocation($input: ArchiveLocationMutationInput!) {
    archiveLocation(input: $input) {
      success
      code
      message
    }
  }
`;

const UNARCHIVE_LOCATION = gql`
  mutation UnarchiveLocation($input: UnarchiveLocationMutationInput!) {
    unarchiveLocation(input: $input) {
      success
      code
      message
    }
  }
`;

type EditLocationModalProps = {
  activeLocationId: string | null;
  clearActiveLocationId(): void;
};

function EditLocationModal({
  activeLocationId,
  clearActiveLocationId,
}: EditLocationModalProps) {
  const [open, setOpen] = useState(true);
  const [formState, setFormState] = useState<EditLocationFormState>({
    address: "",
    coordinates: null,
    name: "",
    arrivalInstructionsQuill: "",
  });

  const [createLocation, { loading: createLoading }] = useMutation<
    CreateLocation,
    CreateLocationVariables
  >(CREATE_LOCATION);
  const [updateLocation, { loading: updateLoading }] = useMutation<
    UpdateLocation,
    UpdateLocationVariables
  >(UPDATE_LOCATION);

  const { setContent } = useFlashMessage();
  const analytics = useAnalytics();

  const { data: locationData, loading: locationLoading } = useQuery<
    FindLocationToEdit,
    FindLocationToEditVariables
  >(FIND_LOCATION, {
    skip: !activeLocationId,
    variables: {
      input: {
        id: activeLocationId ?? "",
      },
    },
  });
  const selectedLocation = locationData?.currentOrganization?.locationById;

  const [archiveLocationMutate] = useMutation<
    ArchiveLocation,
    ArchiveLocationVariables
  >(ARCHIVE_LOCATION);
  const [unarchiveLocationMutate] = useMutation<
    UnarchiveLocation,
    UnarchiveLocationVariables
  >(UNARCHIVE_LOCATION);

  const unarchiveLocation = async (locationId: string): Promise<void> => {
    await unarchiveLocationMutate({
      variables: {
        input: {
          locationId,
        },
      },
      refetchQueries: ["FetchCompanyData"],
    });
    setContent({
      content: "Location unarchived",
      severity: "success",
    });
  };

  const handleArchiveLocation = async (): Promise<void> => {
    if (!activeLocationId) {
      throw new Error("No valid id to archive");
    }
    await archiveLocationMutate({
      variables: {
        input: {
          locationId: activeLocationId,
        },
      },
    });
    const action = (
      <Button
        color="inherit"
        size="small"
        onClick={() => unarchiveLocation(activeLocationId)}
      >
        UNDO
      </Button>
    );
    setContent({
      action,
      content: "Location Archived",
      severity: "success",
    });
    setOpen(false);
  };

  const handleCreateLocation = async (): Promise<void> => {
    if (!formState || !formState.name) {
      setContent({
        content: "Something went wrong creating your location.",
        severity: "error",
      });
      return;
    }
    try {
      await createLocation({
        variables: {
          input: {
            name: formState.name,
            address: formState.address,
            arrivalInstructions: formState.arrivalInstructionsQuill,
            coordinates: formState.coordinates,
          },
        },
      });
      setOpen(false);
      setContent({
        content: "Successfully created your location.",
        severity: "success",
      });
      if (analytics) {
        analytics.track("Company Page Location Created");
      }
    } catch (err) {
      setContent({
        content: "Something went wrong creating your location.",
        severity: "error",
      });
    }
  };

  const handleUpdateLocation = async (): Promise<void> => {
    if (!formState || !activeLocationId) {
      setContent({
        content: "Something went wrong updating your location.",
        severity: "error",
      });
      return;
    }
    try {
      await updateLocation({
        variables: {
          input: {
            locationId: activeLocationId,
            name: formState.name,
            arrivalInstructions: formState.arrivalInstructionsQuill,
            address: formState.address,
            coordinates: formState.coordinates,
          },
        },
      });
      setOpen(false);
      setContent({
        content: "Successfully updated your location.",
        severity: "success",
      });
      if (analytics) {
        analytics.track("Company Page Location updated", {
          locationId: activeLocationId,
        });
      }
    } catch (err) {
      setContent({
        content: "Something went wrong updating your location.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (selectedLocation) {
      setFormState({
        arrivalInstructionsQuill: selectedLocation.arrivalInstructions,
        coordinates:
          selectedLocation?.coordinates?.[0] &&
          selectedLocation?.coordinates?.[1]
            ? {
                latitude: toNumber(selectedLocation.coordinates?.[0]),
                longitude: toNumber(selectedLocation.coordinates?.[1]),
              }
            : null,
        address: selectedLocation.address,
        name: selectedLocation.name,
      });
    } else {
      setFormState({
        arrivalInstructionsQuill: "",
        coordinates: null,
        address: "",
        name: "",
      });
    }
  }, [activeLocationId, selectedLocation]);

  return (
    <Dialog
      open={open}
      fullWidth
      onExited={() => {
        clearActiveLocationId();
      }}
      onClose={(): void => setOpen(false)}
    >
      {locationLoading ? (
        <Loading />
      ) : (
        <>
          <DialogTitle onClose={(): void => setOpen(false)}>
            {activeLocationId ? "Update Location" : "Add a Location"}
          </DialogTitle>
          <Divider />
          <DialogContent>
            {formState && (
              <LocationForm
                formState={formState}
                setFormState={(updatedFormState): void => {
                  setFormState(updatedFormState);
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={createLoading || updateLoading}
              onClick={(): void => {
                if (activeLocationId) {
                  handleUpdateLocation();
                } else {
                  handleCreateLocation();
                }
              }}
            >
              {activeLocationId ? "Update" : "Create"}
            </Button>
            {activeLocationId && (
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                disabled={updateLoading}
                onClick={handleArchiveLocation}
              >
                Archive
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
EditLocationModal.fragments = {
  location: ORGANIZATION_LOCATION_FRAGMENT,
};

export default EditLocationModal;
